/**
 * MAP-626: keeps a translucent link's directionality head the same colour as
 * its line.
 *
 * Cytoscape's canvas renderer clears the pixels underneath an arrowhead before
 * painting it whenever the edge is not fully opaque
 * (`drawArrowhead` in cytoscape/src/extensions/renderer/canvas/drawing-edges.mjs):
 *
 *   if (opacity !== 1 || arrowFill === 'hollow') {
 *     context.globalCompositeOperation = 'destination-out';
 *     ... drawArrowShape(...)   // erases with alpha 1
 *   }
 *
 * The clear only exists so the edge's own line does not show through the
 * translucent head. It is safe when every element owns its texture, but the
 * canvas renderer disables texture caching for edges AND for compound parents
 * (`allowEdgeTxrCaching: false`, `allowParentTxrCaching: false` in
 * cytoscape/src/extensions/renderer/canvas/index.mjs), so both paint straight
 * onto the shared surface. Containers sort before edges, so a dimmed link's
 * head erases the container fill under it and then blends against the app
 * background instead of the container.
 *
 * With filteringOpacity 0.2, link #1E88E5 and container fill #FFD54F:
 *   line over container:            0.2*(30,136,229) + 0.8*(255,213,79) = (210, 197.6, 109)
 *   head with the clear (light bg): 0.2*(30,136,229) + 0.8*(255,255,255) = (210, 231.2, 249.8)
 *   head with the clear (dark bg):  0.2*(30,136,229) + 0.8*(30,30,30)    = (30, 51.2, 69.8)
 * which is why the reporter saw it in both appearances. Dropping the clear
 * makes the head composite against the same backdrop as its line, so both read
 * (210, 197.6, 109).
 *
 * The clear is kept for hollow arrowheads, where punching the interior out is
 * the point of it.
 */

type ArrowFill = 'filled' | 'hollow' | 'both';

type DrawArrowShape = (
  edge: unknown,
  context: CanvasRenderingContext2D,
  fill: ArrowFill,
  ...rest: unknown[]
) => void;

interface PatchableRenderer {
  drawArrowShape?: DrawArrowShape;
  __maporiumTranslucentArrowheads?: boolean;
}

/**
 * Suppresses the destination-out pass for filled arrowheads on a canvas
 * renderer instance. Returns whether the renderer was patched (false when the
 * renderer is missing, already patched, or does not expose the hook - e.g. the
 * WebGL renderer).
 */
export function patchTranslucentArrowheads(renderer: unknown): boolean {
  const target = renderer as PatchableRenderer | null | undefined;
  if (!target || typeof target.drawArrowShape !== 'function') return false;
  if (target.__maporiumTranslucentArrowheads) return false;

  const original = target.drawArrowShape;
  target.drawArrowShape = function (
    this: unknown,
    edge: unknown,
    context: CanvasRenderingContext2D,
    fill: ArrowFill,
    ...rest: unknown[]
  ) {
    if (fill === 'filled' && context?.globalCompositeOperation === 'destination-out') {
      return;
    }
    return original.call(this, edge, context, fill, ...rest);
  };
  target.__maporiumTranslucentArrowheads = true;
  return true;
}

/** Applies {@link patchTranslucentArrowheads} to a cytoscape core's renderer. */
export function installTranslucentArrowheads(cy: unknown): boolean {
  const core = cy as { renderer?: () => unknown } | null | undefined;
  if (!core || typeof core.renderer !== 'function') return false;
  return patchTranslucentArrowheads(core.renderer());
}
